import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  PeopleFill,
  PersonCheckFill,
  Search,
  Shuffle,
  ExclamationTriangleFill,
} from "react-bootstrap-icons";
import styles from "./CaseManagerAssignmentGuide.module.css";
import {
  ACCESS_MODES,
  ASSIGN_STEPS,
  DEVICE_LIST_STEPS,
  GUIDE_FIGURES,
  GUIDE_META,
  IMPORTANT_NOTES,
  MANAGE_PANELS,
  REASSIGN_STEPS,
  SEARCH_TIPS,
  UNASSIGN_STEPS,
} from "./guideData";

const TABS = {
  OVERVIEW: "overview",
  ACCESS: "access",
  MANAGE: "manage",
  ACTIONS: "actions",
  DEVICE_LIST: "device_list",
};

function StepList({ steps }) {
  return (
    <div className={styles.stepList}>
      {steps.map((step, index) => (
        <div key={step} className={styles.stepCard}>
          <span className={styles.stepNumber}>{index + 1}</span>
          <p>{step}</p>
        </div>
      ))}
    </div>
  );
}

function GuideFigure({ src, caption, alt }) {
  return (
    <figure className={styles.figure}>
      <div className={styles.figureFrame}>
        <img src={src} alt={alt || caption || "User guide figure"} className={styles.figureImage} />
      </div>
      {caption ? <figcaption className={styles.figureCaption}>{caption}</figcaption> : null}
    </figure>
  );
}

function CaseManagerAssignmentGuide({ show, onHide }) {
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      className={styles.modalRoot}
      backdropClassName={styles.guideBackdrop}
      dialogClassName={styles.dialog}
      contentClassName={styles.content}
    >
      <Modal.Header closeButton className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerIcon}>
            <PeopleFill size={16} />
          </div>
          <div>
            <Modal.Title className={styles.title}>{GUIDE_META.title}</Modal.Title>
            <p className={styles.subtitle}>{GUIDE_META.subtitle}</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        <section className={styles.introCard}>
          <h4>Overview</h4>
          <p>{GUIDE_META.overview}</p>
        </section>

        <div className={styles.tabHeader}>
          {[
            { id: TABS.OVERVIEW, label: "Overview" },
            { id: TABS.ACCESS, label: "Access Mode" },
            { id: TABS.MANAGE, label: "Manage Assignments" },
            { id: TABS.ACTIONS, label: "Assign / Reassign" },
            { id: TABS.DEVICE_LIST, label: "Device Users List" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`${styles.tabButton} ${
                activeTab === tab.id ? styles.tabButtonActive : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === TABS.OVERVIEW && (
          <div className={styles.tabPanel}>
            <h4 className={styles.sectionTitle}>What this feature does</h4>
            <p className={styles.sectionText}>
              Administrators and Managers can assign, reassign, or unassign Device Users
              for a Case Manager / CRE. Visibility depends on the Client’s Case Manager
              Access Mode.
            </p>
            <div className={styles.panelGrid}>
              <div className={styles.panelCard}>
                <h5><PersonCheckFill className="me-1" size={13} /> From Admin Users</h5>
                <p>
                  Open Manage Device Users Assignment on a Case Manager row to move users
                  between assignment panels and save changes.
                </p>
              </div>
              <div className={styles.panelCard}>
                <h5><Shuffle className="me-1" size={13} /> From Device Users</h5>
                <p>
                  Select one or more Device Users and use Device User Reassignment to
                  assign them to a Case Manager in bulk.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === TABS.ACCESS && (
          <div className={styles.tabPanel}>
            <h4 className={styles.sectionTitle}>2. Case Manager Access Mode</h4>
            <p className={styles.sectionText}>
              The Client’s Case Manager Access Mode determines which Device Users a Case
              Manager can see. Case Manager Access Mode is configured at both the Entity
              and Client levels.
            </p>
            <p className={styles.sectionText}>
              The setting is available under <strong>Client Settings → Manage Care Team Roles</strong>.
            </p>
            <div className={styles.modeGrid}>
              {ACCESS_MODES.map((item) => (
                <article key={item.mode} className={styles.modeCard}>
                  <span
                    className={`${styles.modeBadge} ${
                      item.mode === "ALL" ? styles.modeBadgeAll : styles.modeBadgeGroup
                    }`}
                  >
                    {item.label}
                  </span>
                  <p>{item.visibility}</p>
                </article>
              ))}
            </div>
            <GuideFigure {...GUIDE_FIGURES.accessMode} />
            <div className={styles.exampleBox}>
              <h5>Note</h5>
              <p>
                Existing Device User assignments are retained when the access mode is
                changed and can be used for historical reference.
              </p>
            </div>
          </div>
        )}

        {activeTab === TABS.MANAGE && (
          <div className={styles.tabPanel}>
            <h4 className={styles.sectionTitle}>3. Manage Device User Assignments</h4>
            <h4 className={styles.sectionTitle}>3.1 Open Manage Device Users</h4>
            <StepList
              steps={[
                "Navigate to User Management.",
                "Locate the Case Manager whose Device Users you want to manage.",
                "Click on Manage Device Users Assignment.",
              ]}
            />
            <GuideFigure {...GUIDE_FIGURES.figure1} />

            <h4 className={styles.sectionTitle}>Assignment panels</h4>
            <div className={styles.panelGrid}>
              {MANAGE_PANELS.map((panel) => (
                <article key={panel.title} className={styles.panelCard}>
                  <h5>{panel.title}</h5>
                  <p>{panel.description}</p>
                </article>
              ))}
            </div>
            <GuideFigure {...GUIDE_FIGURES.figure2} />

            <h4 className={styles.sectionTitle}>
              <Search size={13} className="me-1" />
              3.2 Search and identify Device Users
            </h4>
            <ul className={styles.tipList}>
              {SEARCH_TIPS.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        )}

        {activeTab === TABS.ACTIONS && (
          <div className={styles.tabPanel}>
            <h4 className={styles.sectionTitle}>4. Assign, Reassign, and Unassign Device Users</h4>
            <div className={styles.actionGrid}>
              <article className={styles.actionCard}>
                <h5>4.1 Assign</h5>
                <ol>
                  {ASSIGN_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </article>
              <article className={styles.actionCard}>
                <h5>4.2 Reassign</h5>
                <ol>
                  {REASSIGN_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </article>
              <article className={styles.actionCard}>
                <h5>4.3 Unassign</h5>
                <ol>
                  {UNASSIGN_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </article>
            </div>
            <div className={styles.exampleBox}>
              <h5>Example confirmation</h5>
              <p>
                Reassign Device User? This user is currently assigned to Tom Ford. Do you
                want to reassign this user to Jane Doe?
              </p>
            </div>
          </div>
        )}

        {activeTab === TABS.DEVICE_LIST && (
          <div className={styles.tabPanel}>
            <h4 className={styles.sectionTitle}>
              5. Manage Case Manager Assignments from the Device Users List
            </h4>
            <p className={styles.sectionText}>
              Assignments can also be managed directly from the Device Users list using
              bulk Device User Reassignment.
            </p>
            <StepList steps={DEVICE_LIST_STEPS} />
            <GuideFigure {...GUIDE_FIGURES.figure3} />
            <div className={styles.notePanel}>
              <h4>
                <ExclamationTriangleFill size={12} />
                Important notes
              </h4>
              <ul className={styles.noteList}>
                {IMPORTANT_NOTES.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default CaseManagerAssignmentGuide;
